/**
 * Creator discovery behavior from the user perspective.
 * Product rules only — no search indexes, graph queries, or APIs.
 */
export interface CreatorDiscoveryRule {
  readonly id: string;
  readonly rule: string;
  readonly userFacingEffect: string;
}

export const CREATOR_DISCOVERY_RULES: readonly CreatorDiscoveryRule[] = [
  {
    id: 'initial_discovery_surfaces',
    rule: 'Users find creators initially through the public feed, Discover page, and topic-based browse.',
    userFacingEffect:
      'New users browse a chronological feed, explore a creator list, and filter by profile topics.',
  },
  {
    id: 'search_by_name_topics',
    rule: 'Users can search creators by display name, profile bio keywords, and profile topics.',
    userFacingEffect: 'Search helps users narrow the creator list without needing to follow anyone first.',
  },
  {
    id: 'follow_graph_expansion',
    rule: 'The follow graph affects discovery by prioritizing followed creators and their network.',
    userFacingEffect:
      'After you follow a creator, you see more of their content and may see creators they resemble or overlap with.',
  },
  {
    id: 'similar_creators_concept',
    rule: 'Similar creators appear based on shared topics, audience overlap, and engagement patterns.',
    userFacingEffect:
      'On a creator profile or after following, users may see “creators like this” as a conceptual recommendation strip.',
  },
  {
    id: 'creator_profile_in_feed',
    rule: 'Creator profiles can surface in the feed as discovery cards, not only posts.',
    userFacingEffect: 'The feed introduces you to people, not just individual posts.',
  },
  {
    id: 'showcase_on_landing',
    rule: 'Highlighted creators appear on the landing showcase for first-time visitors.',
    userFacingEffect: 'Visitors see a curated creator showcase before signing up.',
  },
];

export type CreatorDiscoveryRules = typeof CREATOR_DISCOVERY_RULES;
